/**
 * Налоговая логика — ядро продукта.
 *
 * Пользователь выбирает один из трёх режимов и вводит ставку руками.
 * Мы всегда показываем три числа: начислено, налог, к выплате — плюс
 * реальный расход организации. Никакой скрытой магии.
 */

import { PayoutKind, TaxMode } from './enums';
import { percentOfMinor } from './money';

export interface TaxInput {
  /** Базовая сумма в минорных единицах — то, что ввёл пользователь. */
  baseMinor: number;
  mode: TaxMode;
  /** Ставка в процентах: 6 = 6%. */
  ratePercent: number;
}

export interface TaxBreakdown {
  /** Что ввёл пользователь. */
  baseMinor: number;
  /** Начислено «грязными» — база, с которой считается налог. */
  grossMinor: number;
  /** Сумма налога. */
  taxMinor: number;
  /** Сколько человек получит на руки. */
  netMinor: number;
  /** Во сколько выплата обходится организации (эта сумма спишется с кошелька). */
  orgCostMinor: number;
  mode: TaxMode;
  ratePercent: number;
}

/**
 * | Режим        | Ввод   | На руки          | Расход организации |
 * |--------------|--------|------------------|--------------------|
 * | ORG_PAYS     | «на руки» B | B           | B + B×ставка       |
 * | PERSON_PAYS  | сумма контракта B | B − B×ставка | B          |
 * | NO_TAX       | B      | B                | B                  |
 */
export function computeTax(input: TaxInput): TaxBreakdown {
  const base = Math.trunc(input.baseMinor);
  const rate = Number.isFinite(input.ratePercent) ? input.ratePercent : 0;
  const safeRate = input.mode === 'NO_TAX' ? 0 : Math.max(0, rate);

  switch (input.mode) {
    case 'ORG_PAYS': {
      const tax = percentOfMinor(base, safeRate);
      return {
        baseMinor: base,
        grossMinor: base + tax,
        taxMinor: tax,
        netMinor: base,
        orgCostMinor: base + tax,
        mode: input.mode,
        ratePercent: safeRate,
      };
    }
    case 'PERSON_PAYS': {
      const tax = percentOfMinor(base, safeRate);
      return {
        baseMinor: base,
        grossMinor: base,
        taxMinor: tax,
        netMinor: base - tax,
        orgCostMinor: base,
        mode: input.mode,
        ratePercent: safeRate,
      };
    }
    case 'NO_TAX':
    default:
      return {
        baseMinor: base,
        grossMinor: base,
        taxMinor: 0,
        netMinor: base,
        orgCostMinor: base,
        mode: 'NO_TAX',
        ratePercent: 0,
      };
  }
}

/**
 * Обратная задача: человек должен получить на руки ровно `netMinor`.
 * Сколько нужно начислить при удержании налога?
 * Используется в подсказке «начислить, чтобы на руки вышло …».
 */
export function grossUpFromNet(netMinor: number, ratePercent: number): number {
  const rate = Math.max(0, Math.min(ratePercent, 99.999)) / 100;
  if (rate === 0) return netMinor;
  return Math.round(netMinor / (1 - rate));
}

/** Вычеты (штрафы) уменьшают выплату, поэтому в расчётах идут со знаком минус. */
export function isDeductionKind(kind: PayoutKind): boolean {
  return kind === 'PENALTY';
}

/** Знак начисления: +1 обычное начисление, −1 вычет. */
export function payoutSign(kind: PayoutKind): 1 | -1 {
  return isDeductionKind(kind) ? -1 : 1;
}

/** Расчёт аванса по правилам контракта. */
export function computeAdvance(
  salaryMinor: number,
  advanceType: 'PERCENT' | 'FIXED',
  advanceValue: number,
): number {
  if (advanceType === 'PERCENT') {
    const percent = Math.max(0, Math.min(advanceValue, 100));
    return percentOfMinor(salaryMinor, percent);
  }
  return Math.max(0, Math.min(Math.trunc(advanceValue), salaryMinor));
}

/**
 * Разбивает месячную зарплату на аванс и остаток.
 * Остаток никогда не уходит в минус.
 */
export function splitSalaryWithAdvance(
  salaryMinor: number,
  advanceMinor: number,
): { advanceMinor: number; remainderMinor: number } {
  const advance = Math.max(0, Math.min(advanceMinor, salaryMinor));
  return { advanceMinor: advance, remainderMinor: salaryMinor - advance };
}
